// src/components/resources/tools/generators/FakeChat/MessengerPreview.tsx

'use client'

import { cn } from '~/lib/utils'
import { useFakeChatState } from './FakeChatContext'
import type { ChatMessage, ChatParticipant } from './FakeChat.types'
import { useResolvedMessages } from './useResolvedMessages'
import { IosStatusBar, statusBarTimeFrom } from './IosStatusBar'

const MESSENGER_BLUE = 'linear-gradient(195deg, #00c6ff 0%, #0078ff 55%, #0063ff 100%)'

interface MessengerBubbleProps {
  message: ChatMessage
  participant?: ChatParticipant
  isSelf: boolean
  isContinuation: boolean
  nextIsSameSender: boolean
  showAvatars: boolean
  showReactions: boolean
  showTimestamps: boolean
  isGroup: boolean
}

/**
 * Single Messenger bubble. Outgoing (self) bubbles use the signature blue
 * gradient and hug the right edge; incoming bubbles are light grey (#f0f0f0)
 * with a small circular avatar shown on the last bubble of a run. Bubble
 * corner radii tighten between grouped messages, mirroring Messenger. In a
 * group thread the sender's name is shown above the first incoming bubble of
 * each run, matching Messenger's group layout.
 */
function MessengerBubble({
  message,
  participant,
  isSelf,
  isContinuation,
  nextIsSameSender,
  showAvatars,
  showReactions,
  showTimestamps,
  isGroup
}: MessengerBubbleProps): JSX.Element {
  // Messenger tightens the radius on the side facing adjacent same-sender
  // bubbles so a run reads as one grouped column.
  const groupRadius = isSelf
    ? cn('rounded-[18px]', isContinuation && 'rounded-tr-[5px]', nextIsSameSender && 'rounded-br-[5px]')
    : cn('rounded-[18px]', isContinuation && 'rounded-tl-[5px]', nextIsSameSender && 'rounded-bl-[5px]')
  const showAvatar = !isSelf && showAvatars && !nextIsSameSender
  const showSenderName = isGroup && !isSelf && !isContinuation && Boolean(participant?.displayName)
  const reactions = showReactions ? message.reactions : []
  const totalReactions = reactions.reduce((sum, r) => sum + r.count, 0)

  return (
    <div className={cn('flex w-full items-end gap-2 px-3', reactions.length > 0 ? 'pb-[16px]' : 'pb-[2px]', isSelf ? 'justify-end' : 'justify-start')}>
      {!isSelf && showAvatars ? (
        <div className="h-7 w-7 shrink-0">
          {showAvatar ? (
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-[11px] font-semibold text-white',
                participant?.avatarColor ?? 'bg-slate-400'
              )}
            >
              {participant?.avatarInitials ?? '?'}
            </div>
          ) : null}
        </div>
      ) : null}
      <div className={cn('flex max-w-[72%] flex-col', isSelf ? 'items-end' : 'items-start')}>
        {showSenderName ? (
          <span className="mb-[2px] ml-[12px] text-[11px] text-[#65676b]">{participant?.displayName}</span>
        ) : null}
        <div className="relative">
          <div
            className={cn(
              'px-[12px] py-[8px] text-[15px] leading-[20px]',
              groupRadius,
              isSelf ? 'text-white' : 'bg-[#f0f0f0] text-[#050505]'
            )}
            style={isSelf ? { background: MESSENGER_BLUE } : undefined}
          >
            <span className="whitespace-pre-wrap break-words">{message.text}</span>
          </div>
          {/* Reaction pill — Messenger overlaps a small white pill on the
              bubble's bottom-outer corner with the emoji(s) and a count. */}
          {reactions.length > 0 ? (
            <div
              className={cn(
                'absolute bottom-[-11px] flex items-center gap-[1px] rounded-full border border-[#e4e6eb] bg-white px-[5px] py-[1px] text-[12px] shadow-sm',
                isSelf ? 'right-[6px]' : 'left-[6px]'
              )}
            >
              {reactions.slice(0, 3).map(r => (
                <span key={r.id}>{r.emoji}</span>
              ))}
              {totalReactions > 1 ? <span className="ml-[1px] text-[11px] text-[#65676b]">{totalReactions}</span> : null}
            </div>
          ) : null}
        </div>
        {/* Timestamp under the last bubble of a run, mirroring Messenger's
            on-tap timestamp that anchors a grouped column. Extra top margin
            when a reaction pill is present so the pill (which overlaps the
            bubble's bottom edge) doesn't sit on top of the timestamp. */}
        {showTimestamps && !nextIsSameSender ? (
          <span className={cn('text-[11px] text-[#8a8d91]', reactions.length > 0 ? 'mt-[10px]' : 'mt-[3px]', isSelf ? 'mr-[4px]' : 'ml-[4px]')}>
            {message.timestamp.trim()}
          </span>
        ) : null}
      </div>
    </div>
  )
}

/**
 * Facebook Messenger platform preview — phone-framed, white canvas, blue
 * gradient outgoing bubbles, grey incoming bubbles with avatars, an
 * "Active now" presence header, and a rounded Messenger composer.
 */
export function MessengerPreview(): JSX.Element {
  const { conversation } = useFakeChatState()
  const resolvedMessages = useResolvedMessages(conversation.messages)
  const contactName = conversation.metadata.channelName || 'Contact'
  const contactInitial = contactName.charAt(0)?.toUpperCase() ?? 'A'
  const statusBarTime = statusBarTimeFrom(resolvedMessages[0]?.message.timestamp)
  const presence = conversation.metadata.lastActivityLabel || 'Active now'
  const isGroup = conversation.metadata.channelType !== 'dm'
  // Messenger surfaces the "Seen" avatar only beneath your most recent
  // message when it's the last in the thread — not after an incoming reply.
  // It follows the avatars toggle like the rest of the preview (the FAQ tells
  // users disabling avatars removes the receipt), and never shows on a message
  // that failed to deliver (a "Seen" under a Not-Delivered bubble is
  // self-contradictory).
  const lastMessage = resolvedMessages[resolvedMessages.length - 1]
  const lastIsSelf = lastMessage?.isSelf ?? false
  const showSeenReceipt =
    lastIsSelf &&
    lastMessage?.message.status !== 'not-delivered' &&
    conversation.metadata.showAvatars &&
    !conversation.metadata.typingIndicator
  // Real Messenger shows the reader's avatar on the "Seen" receipt — i.e. the
  // last non-self participant, not the group/thread title. Fall back to the
  // contact initial for a 1:1 thread where channelName is the person.
  const seenInitial =
    [...conversation.participants].reverse().find(p => !p.isSelf)?.displayName.charAt(0).toUpperCase() ??
    contactInitial

  return (
    <div className="relative flex w-full justify-center p-6">
      <div className="relative h-[760px] w-[360px] rounded-[54px] border border-black/40 bg-black/95 p-4 shadow-[0_30px_70px_rgba(15,23,42,0.35)]">
        <div
          className="relative flex h-full flex-col overflow-hidden rounded-[44px] bg-white"
          style={{ fontFamily: "Helvetica, -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}
        >
          {/* Notch */}
          <div className="absolute left-1/2 top-3 z-20 h-7 w-32 -translate-x-1/2 rounded-full bg-black" aria-hidden />

          {/* Status bar + header — the status bar clears the notch so the
              contact name is never tucked behind the Dynamic Island. */}
          <div className="relative z-10 border-b border-slate-100 bg-white">
            <IosStatusBar time={statusBarTime} tone="dark" />
            <header className="flex items-center gap-2 px-3 pb-2 pt-[3px]">
            <svg viewBox="0 0 24 24" className="h-6 w-5 shrink-0 text-[#0084ff]" fill="none" aria-hidden>
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div className="relative shrink-0">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#00c6ff] to-[#0063ff] text-sm font-semibold text-white">
                {contactInitial}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 h-[11px] w-[11px] rounded-full border-2 border-white bg-[#31a24c]" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[15px] font-semibold leading-tight text-[#050505]">{contactName}</p>
              <p className="truncate text-[12px] leading-tight text-[#65676b]">{presence}</p>
            </div>
            <div className="flex items-center gap-4 pr-1 text-[#0084ff]">
              <svg viewBox="0 0 24 24" className="h-[21px] w-[21px]" fill="currentColor" aria-hidden>
                <path d="M20 15.5a12.5 12.5 0 0 1-3.9-.6 1.1 1.1 0 0 0-1.1.3l-1.7 1.7a13.3 13.3 0 0 1-5.9-5.9l1.7-1.7a1.1 1.1 0 0 0 .3-1.1A12.5 12.5 0 0 1 8.5 4 1.1 1.1 0 0 0 7.5 3H4.4A1.1 1.1 0 0 0 3.3 4 17.7 17.7 0 0 0 20 20.7a1.1 1.1 0 0 0 1-1.1V16.5a1.1 1.1 0 0 0-1-1z" />
              </svg>
              <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="none" aria-hidden>
                <path d="M23 7l-7 5 7 5V7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                <rect x="1" y="5" width="15" height="14" rx="3" stroke="currentColor" strokeWidth="2" />
              </svg>
            </div>
            </header>
          </div>

          {/* Messages */}
          <div className="no-scrollbar relative flex flex-1 flex-col overflow-auto bg-white py-3">
            <div className="flex flex-col items-center gap-2 pb-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#00c6ff] to-[#0063ff] text-2xl font-semibold text-white">
                {contactInitial}
              </div>
              <p className="text-[15px] font-semibold text-[#050505]">{contactName}</p>
              <p className="text-[12px] text-[#65676b]">{conversation.metadata.workspaceName || 'Messenger'}</p>
            </div>
            <div className="flex flex-col gap-[2px]">
              {resolvedMessages.map(({ message, participant, isSelf, isContinuation }, index) => {
                const next = resolvedMessages[index + 1]
                const nextIsSameSender = next?.message.participantId === message.participantId
                return (
                  <MessengerBubble
                    key={message.id}
                    message={message}
                    participant={participant}
                    isSelf={isSelf}
                    isContinuation={isContinuation}
                    nextIsSameSender={nextIsSameSender}
                    showAvatars={conversation.metadata.showAvatars}
                    showReactions={conversation.metadata.showReactions}
                    showTimestamps={conversation.metadata.showTimestamps}
                    isGroup={isGroup}
                  />
                )
              })}
              {conversation.metadata.typingIndicator ? (
                <div className="flex items-end gap-2 px-3 pt-1">
                  {conversation.metadata.showAvatars ? <span className="h-7 w-7 shrink-0" /> : null}
                  <div className="flex items-center gap-[4px] rounded-[18px] bg-[#f0f0f0] px-3 py-[11px]">
                    <span className="h-[8px] w-[8px] animate-pulse rounded-full bg-[#a8abb0]" />
                    <span className="h-[8px] w-[8px] animate-pulse rounded-full bg-[#a8abb0]" style={{ animationDelay: '0.15s' }} />
                    <span className="h-[8px] w-[8px] animate-pulse rounded-full bg-[#a8abb0]" style={{ animationDelay: '0.3s' }} />
                  </div>
                </div>
              ) : null}
            </div>
            {/* Seen receipt — tiny avatar (the reader's) under the last outgoing message */}
            {showSeenReceipt ? (
              <div className="flex justify-end px-3 pt-[3px]">
                <div className="flex h-[14px] w-[14px] items-center justify-center rounded-full bg-gradient-to-br from-[#00c6ff] to-[#0063ff] text-[8px] font-semibold text-white">
                  {seenInitial}
                </div>
              </div>
            ) : null}
          </div>

          {/* Composer */}
          <div className="flex items-center gap-2 bg-white px-3 py-2">
            <svg viewBox="0 0 24 24" className="h-[26px] w-[26px] shrink-0 text-[#0084ff]" fill="currentColor" aria-hidden>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v8M8 12h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <div className="flex flex-1 items-center gap-2 rounded-full bg-[#f0f2f5] px-3 py-[8px]">
              <span className="flex-1 text-[15px] text-[#65676b]">Aa</span>
              <svg viewBox="0 0 24 24" className="h-[20px] w-[20px] text-[#0084ff]" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.5 14s1.3 1.6 3.5 1.6 3.5-1.6 3.5-1.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="9" cy="10" r="1" fill="currentColor" />
                <circle cx="15" cy="10" r="1" fill="currentColor" />
              </svg>
            </div>
            {/* Empty-composer trailing button is Messenger's blue thumbs-up */}
            <svg viewBox="0 0 24 24" className="h-[26px] w-[26px] shrink-0 text-[#0084ff]" fill="currentColor" aria-hidden>
              <path d="M2 20.5h2.2a.8.8 0 0 0 .8-.8v-8.2a.8.8 0 0 0-.8-.8H2zM7 10.6l4.4-7.3a1.9 1.9 0 0 1 3.3 1.9L13.2 9h5.6a2 2 0 0 1 2 2.4l-1.4 6.8a2 2 0 0 1-2 1.6H7z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  )
}
